/**
 * DocumentAccountCard Component
 * Displays parsed account information from a document
 */
import { formatDate } from '@/utils/format';
import { cn } from '@/utils/cn';

export interface DocumentAccountCardProps {
  institution: string;
  accountType: string;
  accountMask: string;
  dateRange?: {
    start: string;
    end: string;
  };
  className?: string;
}

export function DocumentAccountCard({
  institution,
  accountType,
  accountMask,
  dateRange,
  className,
}: DocumentAccountCardProps) {
  // Format account type for display
  const displayAccountType = formatAccountType(accountType);

  // Format account mask
  const displayMask = accountMask ? `****${accountMask}` : '****';

  return (
    <div
      className={cn(
        'flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200',
        className
      )}
    >
      {/* Institution icon */}
      <div className="flex-shrink-0">
        <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
          <BankIcon className="w-6 h-6 text-gray-400" />
        </div>
      </div>

      {/* Account info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-base font-medium text-gray-900 truncate">{institution}</h4>
          <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {displayAccountType}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-600">{displayMask}</p>
        {dateRange && dateRange.start && dateRange.end && (
          <p className="mt-1 text-sm text-gray-500">
            Statement: {formatDateRange(dateRange.start, dateRange.end)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Format account type for display
 */
function formatAccountType(type: string): string {
  const typeMap: Record<string, string> = {
    checking: 'Checking',
    savings: 'Savings',
    credit_card: 'Credit Card',
    credit: 'Credit Card',
    investment: 'Investment',
    loan: 'Loan',
    mortgage: 'Mortgage',
    depository: 'Depository',
    brokerage: 'Brokerage',
  };

  return typeMap[type?.toLowerCase() ?? ''] ?? (type?.charAt(0)?.toUpperCase() ?? '') + (type?.slice(1) ?? '');
}

/**
 * Format date range for display
 */
function formatDateRange(start: string, end: string): string {
  try {
    const startDate = formatDate(start, { month: 'short', day: 'numeric' });
    const endDate = formatDate(end, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  } catch {
    return `${start} - ${end}`;
  }
}

/**
 * Bank icon component
 */
function BankIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z"
      />
    </svg>
  );
}

export default DocumentAccountCard;
